<template>
    <form @submit.prevent="onSubmit" @keydown="form.errors.clear($event.target.name)" enctype="multipart/form-data">
        <div v-for="(fieldName, index) in Object.keys(formFields)" :key="index">
            <div class="form-group">
                <template v-if="formFields[fieldName]['type'] == 'hidden'">
                    <input type="hidden" :name="fieldName">
                </template>

                <template v-else>
                    <div v-if="formFields[fieldName]['type'] == 'checkbox'">
                        <input type="checkbox" :id="fieldName" :name="fieldName" v-model="form[fieldName]" :true-value="1" :false-value="0">
                        <label :for="fieldName">{{ translate('RecordManager.' + recordType + '.' + fieldName) }}</label>
                    </div>

                    <label v-else :for="fieldName">
                        {{ translate('RecordManager.' + recordType + '.' + (formFields[fieldName].hasOwnProperty('primaryTranslationKey') ? formFields[fieldName]['primaryTranslationKey'] : fieldName)) }}
                    </label>

                    <div v-if="formFields[fieldName]['type'] == 'text'">
                        <input type="text" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" :disabled="formFields[fieldName]['enabled'] == false">
                    </div>

                    <div v-else-if="formFields[fieldName]['type'] == 'textarea'">
                        <textarea :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" rows="4" cols="50" :disabled="formFields[fieldName]['enabled'] == false"></textarea>
                    </div>

                    <div v-else-if="formFields[fieldName]['type'] == 'htmleditor'">
                        <template v-if="formFields[fieldName]['editor']['driver'] === 'tinymce'">
                            <tinymce :id="fieldName"
                                     :other_options="{ 'branding': false,
                                                       'statusbar': true,
                                                       'menubar': false,
                                                       'height': formFields[fieldName]['height'] || 300,
                                                       'external_plugins': formFields[fieldName]['editor']['external_plugins'],
                                                       'toolbar1': formFields[fieldName]['editor']['toolbar'],
                                                       'plugins': formFields[fieldName]['editor']['plugins'],
                                                       'inline': false,
                                                       'entity_encoding': 'raw',
                                                       'block_formats': formFields[fieldName]['editor']['blockformats'],
                                                       'style_formats': formFields[fieldName]['editor']['styleformats'],
                                                       'content_css': formFields[fieldName]['editor']['content_css'],
                                                       'indentation': formFields[fieldName]['editor']['indentation'] || '1em' }"
                                     v-model="form[fieldName]"
                                     disabled="formFields[fieldName]['enabled']">
                            </tinymce>
                        </template>
                    </div>

                    <div v-else-if="formFields[fieldName]['type'] == 'select'">
                        <select :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" :disabled="formFields[fieldName]['enabled'] == false">
                            <option v-for="(option, index) in formFields[fieldName]['options']" :value="option.value" :selected="option.value === form[fieldName]" :key="index">{{ option.label }}</option>
                        </select>
                    </div>

                    <div v-else-if="formFields[fieldName]['type'] == 'date'">
                        <input type="date" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" :disabled="formFields[fieldName]['enabled'] == false" :min="formFields[fieldName]['min']" :max="formFields[fieldName]['max']">
                    </div>

                    <div v-else-if="formFields[fieldName]['type'] == 'number'">
                        <input type="number" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" :disabled="formFields[fieldName]['enabled'] == false">
                    </div>

                    <div v-if="formFields[fieldName]['type'] == 'file'">
                        <input type="file" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" @change="onFileChange" :disabled="formFields[fieldName]['enabled'] == false">
                    </div>

                    <div v-if="formFields[fieldName]['type'] == 'multi_file'">
                        <input type="file" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" @change="onMultiFileChange" multiple :disabled="formFields[fieldName]['enabled'] == false">
                    </div>

                    <div v-if="formFields[fieldName]['type'] == 'password_confirmation'">
                        <input type="password" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" :disabled="formFields[fieldName]['enabled'] == false">
                    </div>

                    <div v-if="formFields[fieldName]['type'] == 'password'">
                        <input type="password" :id="fieldName" :name="fieldName" :class="getFormFieldClasses(fieldName)" v-model="form[fieldName]" :disabled="formFields[fieldName]['enabled'] == false">
                    </div>

                    <span class="text-danger" v-if="form.errors.has(fieldName)" v-text="form.errors.get(fieldName)"></span>
                </template>
            </div>
        </div>

        <button type="submit" class="btn btn-success">
            <slot name="submit-button-label">
                <i class="far fa-save" aria-hidden="true"></i> {{ translate('RecordManager.form.save') }}
            </slot>
        </button>

        <slot name="cancel-button">
            <button @click.prevent="onCancel" class="btn btn-light"><i class="fa fa-times" aria-hidden="true"></i> {{ translate('RecordManager.form.cancel') }}</button>
        </slot>
    </form>
</template>

<script>
    export default {
        props: {
            id: { type: Number, required: true },
            formFields: { type: Object, required: true },
            recordType: { type: String, required: true }
        },

        data() {
            return {
                form: undefined
            }
        },

        created() {
            this.form = new Form(this.formData);

            EventDispatcher.listen('setFocusOnField', (fieldName) => {
                if(fieldName !== null) {
                    document.getElementById(fieldName).focus();
                }
            });
        },

        mounted() {
            // set content of html editor fields after component is mounted
            Object.entries(this.formFields).forEach(
                ([id, values]) => {
                    if (values.type === 'htmleditor' && values.editor.driver === 'tinymce') {
                        tinymce.editors[id].setContent(values.value);
                    }
                }
            );
        },

        computed: {
            formData() {
                var formData = {};
                for (var fieldName in this.formFields) {
                    formData[fieldName] = this.formFields[fieldName]['value'];
                }
                return formData;
            }
        },

        methods: {
            onSubmit() {
                EventDispatcher.fire('recordFormSubmitted' + this.id, this.form);
            },

            onFileChange(event) {
                this.form.errors.clear(event.target.name);
                this.form[event.target.name] = event.target.files[0];
            },

            onMultiFileChange(event) {
                EventDispatcher.fire('multiFilesChanged', event.target.files);
                this.form.errors.clear(event.target.name);
                this.form[event.target.name] = event.target.files;
            },

            onCancel() {
                EventDispatcher.fire('recordFormCanceled' + this.id);
            },

            getFormFieldClasses(fieldName) {
                return this.form.errors.has(fieldName) ? 'form-control error' : 'form-control';
            }
        }
    }
</script>

<style lang="scss" scoped>
    .text-danger {
        display: block;
        margin: 0.5rem 0 0 0.5rem;
    }

    input[type="date"] {
        max-width: 200px;
    }

    input[type="number"] {
        max-width: 100px;
    }

    .form-group {
        margin-bottom: 24px;

        label {
            margin-bottom: 4px;
            font-weight: 600;
        }
    }

    .form-control.error {
        border: 1px solid #bf5329;
    }
</style>
